import { COMPANY_INFO } from '@repo/api-contracts';
import { type ReactElement, useId } from 'react';
import { useSiteSettingsQuery } from '@/entities/site-setting';
import { env } from '@/shared/config';

const LINK_CLASS =
  'font-medium text-foreground underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * 상품 상세 우측 패널 끝의 "스토어에 없는 상품이 필요하신가요?" 안내 — 디자인 없는 일반 상담은 폼 없이 연락처로 받는다
 * (비회원 견적 요청 설계 G20). 카카오톡 채널 줄은 운영 설정의 채널 주소(1:1 채팅, 카카오톡 상담 버튼과 같은 값)가 있을 때 보이고,
 * 없으면 web 빌드 값 `VITE_KAKAO_CHANNEL_URL` 로 대신한다(운영 설정 설계 K7 — 빌드 값은 한 릴리스 뒤 지운다).
 * 전화·이메일·상담 시간의 원천은 `COMPANY_INFO` 다(푸터·회사 소개·견적 발행 설정과 같은 값).
 */
export const CustomProductInquiry = (): ReactElement => {
  const headingId = useId();
  const settingsQuery = useSiteSettingsQuery();
  const kakaoChannelUrl = settingsQuery.data?.kakaoChannelChatUrl ?? env.VITE_KAKAO_CHANNEL_URL;
  const consultationHours = COMPANY_INFO.consultationHours.trim();

  return (
    <aside aria-labelledby={headingId} className="border border-foreground/15 p-4 text-sm">
      <h2 id={headingId} className="font-semibold text-foreground">
        스토어에 없는 상품이 필요하신가요?
      </h2>
      <p className="mt-1 text-muted-foreground">
        원하는 품목과 수량을 알려 주시면 제작 가능 여부와 견적을 안내해 드립니다.
      </p>

      <dl className="mt-3 space-y-1.5">
        {kakaoChannelUrl === undefined ? null : (
          <div className="flex gap-3">
            <dt className="w-16 shrink-0 text-muted-foreground">카카오톡</dt>
            <dd className="min-w-0">
              <a
                href={kakaoChannelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={LINK_CLASS}
              >
                카카오톡 채널로 상담하기
              </a>
            </dd>
          </div>
        )}
        <div className="flex gap-3">
          <dt className="w-16 shrink-0 text-muted-foreground">전화</dt>
          <dd className="min-w-0 tabular-nums">
            <a href={`tel:${COMPANY_INFO.phone}`} className={LINK_CLASS}>
              {COMPANY_INFO.phone}
            </a>
          </dd>
        </div>
        <div className="flex gap-3">
          <dt className="w-16 shrink-0 text-muted-foreground">이메일</dt>
          <dd className="min-w-0 break-all">
            <a href={`mailto:${COMPANY_INFO.email}`} className={LINK_CLASS}>
              {COMPANY_INFO.email}
            </a>
          </dd>
        </div>
        {consultationHours === '' ? null : (
          <div className="flex gap-3">
            <dt className="w-16 shrink-0 text-muted-foreground">상담 시간</dt>
            <dd className="min-w-0 text-muted-foreground">{consultationHours}</dd>
          </div>
        )}
      </dl>
    </aside>
  );
};
